import { Component } from '@theme/component';

/**
 * A custom element that manages a tabbed mega-menu panel.
 *
 * Tab triggers live in the left `.mega-menu-tabs__nav` column and are decorated
 * with `data-tab-index`. Their matching right-column panels carry `data-panel-index`.
 *
 * Interaction is driven by `on:pointerenter="/activateTab"` and
 * `on:focus="/activateTab"` on each nav item, delegating to this component
 * following the same convention used by `<header-menu>`.
 *
 * @extends {Component}
 */
class MegaMenuTabs extends Component {
  /**
   * Activate the tab panel corresponding to the nav item that was hovered or focused.
   * Called via `on:pointerenter="/activateTab"` and `on:focus="/activateTab"`.
   *
   * @param {PointerEvent | FocusEvent} event
   */
  activateTab = (event) => {
    if (!(event.target instanceof Element)) return;

    // Resolve the tab link whether the target is the anchor, a child span,
    // or the wrapping <li> nav item.
    const tab =
      event.target.closest('[data-tab-index]') ??
      event.target.querySelector?.('[data-tab-index]');

    if (!(tab instanceof HTMLElement)) return;

    const index = parseInt(tab.dataset.tabIndex ?? '', 10);
    if (Number.isNaN(index)) return;

    this.#activateTabAtIndex(index);
  };

  /**
   * Activate the tab/panel pair at the given sequential index.
   * Updates `aria-selected` on tab links and `hidden` on panels.
   *
   * @param {number} index
   */
  #activateTabAtIndex(index) {
    const tabs = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('[data-tab-index]'))
    );
    const panels = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('[data-panel-index]'))
    );

    for (const tab of tabs) {
      const isActive = parseInt(tab.dataset.tabIndex ?? '', 10) === index;
      tab.setAttribute('aria-selected', String(isActive));
      if (isActive) {
        tab.setAttribute('data-active', '');
      } else {
        tab.removeAttribute('data-active');
      }
    }

    for (const panel of panels) {
      const isActive = parseInt(panel.dataset.panelIndex ?? '', 10) === index;
      panel.classList.toggle('mega-menu-tabs__panel--active', isActive);
      if (isActive) {
        panel.removeAttribute('hidden');
        // Trigger loading of any images that were kept lazy while the panel was hidden
        panel.querySelectorAll('img[loading="lazy"]').forEach((img) => img.removeAttribute('loading'));
      } else {
        panel.setAttribute('hidden', '');
      }
    }
  }
}

if (!customElements.get('mega-menu-tabs')) {
  customElements.define('mega-menu-tabs', MegaMenuTabs);
}
